/**
 * Amazon Sign-In Button Component
 * Handles Login with Amazon OAuth flow using Amazon SDK
 */

'use client';

import React, { useEffect, useState, useCallback } from 'react';
import Image from 'next/image';
import { env } from '@/env/env';
import { useAuth } from '../providers/AuthProvider';
import amazonLogo from '@Assets/icons/amazon-logo.svg';

interface AmazonSignInButtonProps {
    className?: string;
    disabled?: boolean;
}

const AMAZON_SDK_URL = env('NEXT_PUBLIC_AMAZON_SDK_URL') ?? 'https://assets.loginwithamazon.com/sdk/na/login1.js';
const AMAZON_CLIENT_ID = env('NEXT_PUBLIC_AMAZON_CLIENT_ID') ?? '';

/**
 * Load Amazon Login SDK script
 */
function loadAmazonScript(): Promise<void> {
    return new Promise((resolve, reject) => {
        if (window.amazon?.Login) {
            resolve();
            return;
        }

        const existingScript = document.getElementById('amazon-login-script');
        if (existingScript) {
            existingScript.addEventListener('load', () => resolve());
            existingScript.addEventListener('error', () => reject(new Error('Failed to load Amazon SDK')));
            return;
        }

        const script = document.createElement('script');
        script.id = 'amazon-login-script';
        script.src = AMAZON_SDK_URL;
        script.async = true;

        script.onload = () => {
            if (window.amazon?.Login) {
                window.amazon.Login.setClientId(AMAZON_CLIENT_ID);
                resolve();
            } else {
                reject(new Error('Amazon Login API not available'));
            }
        };

        script.onerror = () => reject(new Error('Failed to load Amazon SDK'));

        document.head.appendChild(script);
    });
}

export const AmazonSignInButton: React.FC<AmazonSignInButtonProps> = ({
    className = '',
    disabled = false,
}) => {
    const { loginWithAmazon } = useAuth();
    const [sdkReady, setSdkReady] = useState(false);
    const [sdkFailed, setSdkFailed] = useState(false);

    useEffect(() => {
        loadAmazonScript()
            .then(() => setSdkReady(true))
            .catch((err) => {
                console.error('Amazon SDK load error:', err);
                setSdkFailed(true);
            });
    }, []);

    const handleClick = useCallback(() => {
        if (!sdkReady || disabled || !window.amazon?.Login) return;

        window.amazon.Login.authorize(
            { scope: 'profile', response_type: 'token' },
            (response) => {
                if (response.error) {
                    console.error('Amazon login error:', response.error_description || response.error);
                    return;
                }

                if (response.access_token) {
                    loginWithAmazon(response.access_token);
                }
            }
        );
    }, [sdkReady, disabled, loginWithAmazon]);

    if (sdkFailed) return null;

    return (
        <div
            className={className}
            onClick={handleClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(); }}
            style={{ cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1 }}
        >
            <Image src={amazonLogo} width={24} height={24} alt="Amazon" />
        </div>
    );
};

export default AmazonSignInButton;
